// @ts-nocheck
import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';


declare const $: any;

@Component({
  selector: 'app-select2',
  templateUrl: './select2.component.html',
  styleUrls: ['./select2.component.css'],
  encapsulation: ViewEncapsulation.None
})

/**
 * Component to wrap usage of select2 jquery component
 */
export class Select2Component implements AfterViewInit, AfterContentChecked, OnDestroy {
  /**
   * Element ref to main component element
   */
  @ViewChild('cmpEl') cmpEl: ElementRef;

  /**
   * Element ref to select2 element
   */
  @ViewChild('select2') select2El: ElementRef;

  /**
   * Select with multiple options?
   */
  @Input() multiple: boolean;

  /**
   * Label to show
   */
  @Input() label = '';

  /**
   * Should we hide the label?
   */
  @Input() hideLabel = false;

  /**
   * Should we hide the button?
   */
  @Input() hideButton = false;

  /**
   * Font awesome icon for button
   */
  @Input() buttonFaIcon = 'fa-plus';

  /**
   * Should we show the button?
   */
  @Input() showDeleteButton = false;

  /**
   * Font awesome icon for delete button
   */
  @Input() deleteButtonFaIcon = 'fa-trash';

  /**
   * String with css classes for select html element
   */
  @Input() selectCssClasses = '';

  /**
   * String with css classes for component html element
   */
  @Input() cmpCssClasses = '';

  /**
   * String with css classes for button html element
   */
  @Input() btnCssClasses = 'btn-success';

  /**
   * Should we disable submit on enter key?
   */
  @Input() disableSubmitOnEnter = false;


  /**
   * Default select2 options
   */
  @Input() options = {width: '100%', placeholder: '...'};

  /**
   * Select2 events to add
   */
  @Input() events: {[eventName: string]: Function} = {};

  /**
   * Event emitter which transmits the component element ref
   */
  @Output() cmpElement: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

  /**
   * Event emitter which transmits the select element ref
   */
  @Output() selectElement: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

  /**
   * Event emitter for when button was clicked
   */
  @Output() click: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event emitter for when selection was deselected
   */
  @Output() deselect: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event emitter for when selection was submitted (via button press or enter key)
   */
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Currently selected values. You can use this for two-way data binding
   */
  @Input() set selected(value: any) {
    this._selected = value;
    this.applySelectedValue();
  }

  /**
   * Change event emitter for two way data binding
   */
  @Output() selectedChange: EventEmitter<any> = new EventEmitter<any>();

  private _selected: any;
  private select2Initialized = false;
  private isProgrammaticUpdate = false;
  private isRefreshing = false;
  private lastOptionsSignature: string | null = null;
  private pendingContentRefreshTimeout: any = null;

  /**
   * Initializes component
   */
  public ngAfterViewInit() {
    this.cmpElement.emit(this.cmpEl);
    this.selectElement.emit(this.select2El);
    this.renderSelect2();
    this.select2Initialized = true;
    this.applySelectedValue();
    this.lastOptionsSignature = this.getOptionsSignature();
  }

  public ngAfterContentChecked(): void {
    if (!this.select2Initialized || this.isRefreshing) {
      return;
    }

    const signature = this.getOptionsSignature();
    if (signature === this.lastOptionsSignature) {
      return;
    }

    this.lastOptionsSignature = signature;
    this.scheduleContentRefresh();
  }

  public ngOnDestroy(): void {
    if (this.pendingContentRefreshTimeout) {
      clearTimeout(this.pendingContentRefreshTimeout);
      this.pendingContentRefreshTimeout = null;
    }
    this.destroySelect2();
  }

  /**
   * Emits click and submit events
   */
  public onSubmit() {
    this.click.emit();
    const isArray = Array.isArray(this._selected);

    if (isArray && this._selected.length || !isArray && this._selected) {
      this.submit.emit(this._selected);
    }
  }

  /**
   * Deletes selected value
   */
  public onDelete() {
    this._selected = null;
    $(this.select2El.nativeElement).val(null).trigger('change');
    this.selectedChange.emit(this._selected);
    this.deselect.emit();
  }

  /**
   * Applies the currently selected value to the select2 element.
   */
  private applySelectedValue(): void {
    if (!this.select2El || !this.select2El.nativeElement || !this.select2Initialized) {
      return;
    }

    const select = $(this.select2El.nativeElement);

    // avoid feedback loops when parent updates selected via binding
    const currentValue = select.val();
    try {
      if (JSON.stringify(currentValue) === JSON.stringify(this._selected)) {
        return;
      }
    } catch (e) {
      // ignore
    }

    // use change.select2 to notify select2 about changes
    this.isProgrammaticUpdate = true;
    try {
      select.val(this._selected).trigger('change.select2');
    } finally {
      // release in next tick so select2 can finish internal processing
      setTimeout(() => this.isProgrammaticUpdate = false, 0);
    }
  }

  private scheduleContentRefresh(): void {
    if (this.pendingContentRefreshTimeout) {
      return;
    }

    // debounce multiple content checks in same tick
    this.pendingContentRefreshTimeout = setTimeout(() => {
      this.pendingContentRefreshTimeout = null;
      this.refreshSelect2();
    }, 0);
  }

  private refreshSelect2(): void {
    if (!this.select2El || !this.select2El.nativeElement) {
      return;
    }

    const select = $(this.select2El.nativeElement);
    const selectedBefore = this._selected;

    this.isRefreshing = true;

    try {
      // destroy and re-init to force select2 to re-read <option> labels
      select.off('.select2Component');
      select.select2('destroy');
    } catch (e) {
      // ignore
    }

    this.renderSelect2();
    this._selected = selectedBefore;
    this.applySelectedValue();
    this.isRefreshing = false;
  }

  /**
   * Builds a stable signature for the current <option> list.
   * We only use value + text to avoid loops caused by select2-added attributes.
   */
  private getOptionsSignature(): string {
    if (!this.select2El || !this.select2El.nativeElement) {
      return '';
    }

    const selectEl: HTMLSelectElement = this.select2El.nativeElement;
    const parts: string[] = [];

    // selectEl.options contains <option> and <optgroup> option entries already flattened
    for (let i = 0; i < selectEl.options.length; i++) {
      const opt = selectEl.options.item(i);
      if (!opt) {
        continue;
      }
      parts.push(`${opt.value}::${opt.text}`);
    }

    return parts.join('||');
  }

  private destroySelect2(): void {
    if (!this.select2El || !this.select2El.nativeElement) {
      return;
    }
    const select = $(this.select2El.nativeElement);
    try {
      select.off('.select2Component');
      select.select2('destroy');
    } catch (e) {
      // ignore
    }
  }

  /**
   * Converts normal select html element into dynamic select2 element
   */
  private renderSelect2() {
    const cmp = this;
    const select = $(this.select2El.nativeElement);

    // convert selects
    select.select2(this.options);

    // focuses search input field when select2 was opened
    select.on('select2:open.select2Component', function() {
      const dropDownSearchEl: HTMLElement = document.querySelector('.select2-dropdown input.select2-search__field');

      if (dropDownSearchEl) {
        dropDownSearchEl.focus();
        return;
      }

      const searchEl: HTMLElement = select[0].querySelector('.select2-container input.select2-search__field');
      searchEl && searchEl.focus();
    })

    // sets selected property
    select.on('select2:select.select2Component', function() {
      if (cmp.isProgrammaticUpdate) {
        return;
      }
      // @ts-ignore
      cmp._selected = $(this).val();
      cmp.selectedChange.emit(cmp._selected);
    });

    // sets selected property
    select.on('select2:unselect.select2Component', function() {
      if (cmp.isProgrammaticUpdate) {
        return;
      }
      cmp._selected = $(this).val();
      cmp.selectedChange.emit(cmp._selected);
    });

    // stupid hack to remove unwanted tooltips
    $('.select2-selection__rendered').hover(function () {
      $(this).removeAttr('title');
    });

    // stupid hack to make select2 el auto scale the width
    setTimeout(() => $('.select2-search__field').css('width', 'auto'), 800);

    // register custom select2 events
    if (typeof this.events === 'object') {
      for (const eventName of Object.keys(this.events)) {
        const fn = this.events[eventName];
        (typeof fn === 'function') && select.on(eventName, fn);
      }
    }

    if (this.disableSubmitOnEnter) {
      return;
    }

    /**
     * Hack to trigger submit, when selection was made via enter key.
     */
    select.on('select2:open.select2Component', () => {
      const dropdown = $('.select2-dropdown');

      if (!dropdown.length) {
        return;
      }

      const searchField = $('.select2-dropdown .select2-search__field');
      searchField.unbind('keydown.submitOnEnterCustom');
      // NOTE: returning false will stop propagation for all registered key down event handler!
      searchField.bind('keydown.submitOnEnterCustom', (event) => (event.which === 13) && this.onSubmit() || true);
    });
  }
}
